import { ChevronDown, ChevronUp, KeyRound, Plug } from 'lucide-react';
import { useMemo, useState, Fragment } from 'react';

import {
  AdminBulkActionRow,
  AdminSectionCard,
  ConfirmDialog,
  EmptyState,
  MarketplaceIcon,
  Modal,
  Pill,
  Switch,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { ADMIN_ACTION_BUTTON, BUTTON_PRIMARY, FIELD_INPUT } from '@/shared/ui-class';
import {
  ADMIN_SEED_ANCHOR_MS,
  SEED_ADMIN_CONNECTORS,
  type AdminConnectorSummary,
} from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell } from './admin-shared';
import {
  authPillTone,
  connectorAuthPill,
  filterConnectors,
  formatOAuthCredentialSet,
  hasStaticOAuth,
  maskedOAuthClientId,
  maskedOAuthClientSecret,
  oauthCallbackUrl,
  usageLabel,
} from './ConnectorsScene.transform';

const MOCK_OAUTH_ROTATE_LAST4 = 'K8m2';

export function ConnectorsScene() {
  const [connectors, setConnectors] = useState(SEED_ADMIN_CONNECTORS);
  const [query, setQuery] = useState('');
  const [allowAll, setAllowAll] = useState(true);
  const [resetOpen, setResetOpen] = useState(false);
  const [whoList, setWhoList] = useState<AdminConnectorSummary | null>(null);
  const [expandedOauthId, setExpandedOauthId] = useState<string | null>(null);

  const filteredConnectors = useMemo(
    () => filterConnectors(connectors, query),
    [connectors, query],
  );

  const toggleConnector = (connectorId: string, enabled: boolean) => {
    setConnectors((prev) =>
      prev.map((connector) =>
        connector.id === connectorId ? { ...connector, enabled } : connector,
      ),
    );
  };

  const rotateOAuthCredentials = (connectorId: string) => {
    setConnectors((prev) =>
      prev.map((connector) => {
        if (connector.id !== connectorId) return connector;
        return {
          ...connector,
          oauthClientLast4: MOCK_OAUTH_ROTATE_LAST4,
          oauthClientSetAt: ADMIN_SEED_ANCHOR_MS,
          oauthClientSetBy: 'alex@example.com',
        };
      }),
    );
  };

  return (
    <AdminSceneShell
      sceneName="AdminConnectorsScene"
      title="Connectors"
      description="Control which marketplace connectors users can install. Manual connectors are always allowed."
      stackClassName="space-y-4"
    >
      <Commentable name="Filters">
        <div className="space-y-3">
          <AdminBulkActionRow
            left={
              <span>
                {allowAll ? 'Allow all connectors' : `${filteredConnectors.length} in allowlist`}
              </span>
            }
          >
            <button
              type="button"
              onClick={() => setResetOpen(true)}
              className={ADMIN_ACTION_BUTTON}
            >
              Reset to default (allow all)
            </button>
          </AdminBulkActionRow>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search connectors…"
            className={cn('w-full', FIELD_INPUT)}
          />
        </div>
      </Commentable>

      <Commentable name="Table">
        <div>
          <AdminSectionCard>
            {filteredConnectors.length === 0 ? (
              <EmptyState
                embedded
                icon={<Plug className="h-8 w-8 text-zinc-400" />}
                title="No connectors match"
              />
            ) : (
              <Table wrap={false}>
                <THead>
                  <TR>
                    <TH>Connector</TH>
                    <TH>ID</TH>
                    <TH>Auth</TH>
                    <TH className="text-right">Usage</TH>
                    <TH className="text-right">Enabled</TH>
                  </TR>
                </THead>
                <TBody>
                  {filteredConnectors.map((connector: AdminConnectorSummary) => {
                    const authLabel = connectorAuthPill(connector);
                    const isOauthExpanded = expandedOauthId === connector.id;
                    const showOauth = hasStaticOAuth(connector);
                    return (
                      <Fragment key={connector.id}>
                        <TR>
                          <TD>
                            <div className="flex items-center gap-2.5">
                              <MarketplaceIcon
                                name={connector.name}
                                logoUrl={connector.logoUrl}
                                invertInDark={connector.logoInvertInDark}
                                size={28}
                              />
                              <div className="min-w-0">
                                <span className="font-medium text-zinc-900 dark:text-zinc-100">
                                  {connector.name}
                                </span>
                                {showOauth ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setExpandedOauthId(
                                        isOauthExpanded ? null : connector.id,
                                      )
                                    }
                                    className={cn(
                                      ADMIN_ACTION_BUTTON,
                                      'ml-2 inline-flex items-center gap-0.5',
                                    )}
                                    title={
                                      isOauthExpanded
                                        ? 'Hide OAuth credentials'
                                        : 'Configure OAuth credentials'
                                    }
                                  >
                                    <KeyRound className="h-3 w-3" />
                                    OAuth creds
                                    {isOauthExpanded ? (
                                      <ChevronUp className="h-3 w-3" />
                                    ) : (
                                      <ChevronDown className="h-3 w-3" />
                                    )}
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </TD>
                          <TD>
                            <code className="text-xs text-muted">
                              {connector.marketplaceId}
                            </code>
                          </TD>
                          <TD>
                            <Pill tone={authPillTone(authLabel)}>{authLabel}</Pill>
                          </TD>
                          <TD className="text-right text-xs text-muted">
                            {connector.installCount > 0 ? (
                              <button
                                type="button"
                                onClick={() => setWhoList(connector)}
                                className="rounded-md border border-zinc-200 px-2 py-0.5 text-xs transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
                              >
                                {usageLabel(connector.installCount)}
                              </button>
                            ) : (
                              usageLabel(connector.installCount)
                            )}
                          </TD>
                          <TD className="text-right">
                            <div className="flex items-center justify-end">
                              <Switch
                                checked={connector.enabled}
                                onCheckedChange={(next) =>
                                  toggleConnector(connector.id, next)
                                }
                                aria-label={`${connector.enabled ? 'Disable' : 'Enable'} ${connector.name}`}
                              />
                            </div>
                          </TD>
                        </TR>
                        {showOauth && isOauthExpanded ? (
                          <TR key={`${connector.id}-oauth`}>
                            <TD
                              colSpan={5}
                              className="border-l-2 border-zinc-300 bg-zinc-50 py-4 pl-5 pr-4 dark:border-zinc-600 dark:bg-zinc-900/40"
                            >
                              <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                  <KeyRound className="h-4 w-4 text-zinc-400" />
                                  <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                                    OAuth App credentials
                                  </span>
                                  <Pill tone="success">Admin set</Pill>
                                </div>

                                <div className="space-y-1">
                                  <p className="text-xs font-medium text-muted">
                                    Callback URL (register this with your OAuth App)
                                  </p>
                                  <code className="block select-all overflow-x-auto whitespace-nowrap rounded-lg border border-divider bg-input px-3 py-1.5 text-xs text-zinc-700 dark:text-zinc-300">
                                    {oauthCallbackUrl(connector.id)}
                                  </code>
                                </div>

                                <div className="text-xs text-muted">
                                  Credentials end in{' '}
                                  <code className="font-mono font-medium">
                                    {maskedOAuthClientId(
                                      connector.oauthClientLast4 ?? '0000',
                                    )}
                                  </code>
                                  , last set{' '}
                                  {connector.oauthClientSetAt
                                    ? formatOAuthCredentialSet(connector.oauthClientSetAt)
                                    : '—'}{' '}
                                  by {connector.oauthClientSetBy ?? 'alex@example.com'}
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <label className="flex flex-col gap-1">
                                    <span className="text-xs font-medium text-muted">
                                      Client ID
                                    </span>
                                    <input
                                      type="text"
                                      readOnly
                                      value={`Ov23li${maskedOAuthClientId(
                                        connector.oauthClientLast4 ?? '0000',
                                      )}`}
                                      className={cn(FIELD_INPUT, 'font-mono text-sm')}
                                      autoComplete="off"
                                    />
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-xs font-medium text-muted">
                                      Client Secret
                                    </span>
                                    <input
                                      type="password"
                                      readOnly
                                      value={maskedOAuthClientSecret()}
                                      className={cn(FIELD_INPUT, 'font-mono text-sm')}
                                      autoComplete="off"
                                    />
                                  </label>
                                </div>

                                <button
                                  type="button"
                                  className={BUTTON_PRIMARY}
                                  onClick={() => rotateOAuthCredentials(connector.id)}
                                >
                                  Rotate credentials
                                </button>
                              </div>
                            </TD>
                          </TR>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </TBody>
              </Table>
            )}
          </AdminSectionCard>
        </div>
      </Commentable>

      <ConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title="Reset to allow all?"
        description="Every marketplace connector will be available to install again."
        confirmLabel="Reset"
        onConfirm={() => {
          setAllowAll(true);
          setConnectors((prev) => prev.map((c) => ({ ...c, enabled: true })));
        }}
      />

      <Modal
        open={whoList !== null}
        onOpenChange={(open) => {
          if (!open) setWhoList(null);
        }}
        title={whoList ? `${whoList.name} — connected users` : 'Connected users'}
      >
        {whoList ? (
          <ul className="space-y-3">
            {(whoList.connectedUsers ?? []).map((user) => (
              <li key={user.email} className="text-sm">
                <div className="font-medium text-zinc-900 dark:text-zinc-100">
                  {user.name}
                </div>
                <div className="text-muted">{user.email}</div>
              </li>
            ))}
          </ul>
        ) : null}
      </Modal>
    </AdminSceneShell>
  );
}
